/**
 * WF-3 · Permission Matrix.
 *
 * Rows = employees, columns = modules. Click a cell to edit that employee's
 * access via the Visual Access Manager slide-over.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, X, ShieldCheck, Filter, Check } from "lucide-react";
import { v3API } from "./v3Api";
import VisualAccessManager from "./VisualAccessManager";

export default function PermissionMatrix() {
  const [data, setData]           = useState({ employees: [], modules: [], grid: {} });
  const [loading, setLoading]     = useState(true);
  const [query, setQuery]         = useState("");
  const [group, setGroup]         = useState("all");
  const [editUser, setEditUser]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await v3API.matrix();
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load matrix");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const groupOptions = useMemo(() => {
    const s = new Set(data.modules.map((m) => m.group));
    return ["all", ...Array.from(s)];
  }, [data.modules]);

  const filteredModules = useMemo(() => {
    if (group === "all") return data.modules;
    return data.modules.filter((m) => m.group === group);
  }, [data.modules, group]);

  const filteredEmployees = useMemo(() => {
    if (!query.trim()) return data.employees;
    const q = query.toLowerCase();
    return data.employees.filter((e) =>
      `${e.employee_name} ${e.email} ${e.role_template_name || ""}`.toLowerCase().includes(q));
  }, [data.employees, query]);

  return (
    <div className="p-6 space-y-4" data-testid="wf-matrix">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[20px] font-semibold text-hocp-navy flex items-center gap-2">
            <ShieldCheck className="w-5 h-5"/> Permission Matrix
          </h1>
          <p className="text-[12px] text-slate-500">
            One row per employee, one column per module. Click a cell to open the Visual Access Manager for that person.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400">
          {filteredEmployees.length} employees · {filteredModules.length} modules
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="matrix-search" placeholder="Search employee / email / role…"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy"/>
        </div>
        <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="matrix-group"
          className="h-9 px-3 rounded-md border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy">
          {groupOptions.map((g) => (
            <option key={g} value={g}>{g === "all" ? "All groups" : g}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400 text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2"/> Loading…
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-auto max-h-[70vh]">
          <table className="w-full text-[12px] border-collapse">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr>
                <th className="text-left px-3 py-2 min-w-[220px] sticky left-0 bg-slate-50 z-20 border-b border-slate-200">Employee</th>
                {filteredModules.map((m) => (
                  <th key={m.id} className="text-center px-2 py-2 border-b border-slate-200 whitespace-nowrap"
                    title={`${m.group} · ${m.name}`}>
                    <div className="text-[10.5px] text-slate-500 uppercase tracking-wider">{m.group}</div>
                    <div className="font-semibold text-hocp-navy">{m.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((e, idx) => (
                <tr key={e.user_id} className={idx % 2 ? "bg-slate-50/40" : ""}
                  data-testid={`matrix-row-${e.user_id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-inherit border-r border-slate-100">
                    <div className="font-medium text-slate-800">{e.employee_name}</div>
                    <div className="text-[10.5px] text-slate-400 truncate max-w-[220px]">{e.email}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {e.is_super_admin
                        ? <span className="text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold">Super Admin</span>
                        : (e.role_template_name || <span className="italic text-slate-300">Custom</span>)}
                    </div>
                  </td>
                  {filteredModules.map((m) => {
                    const on = data.grid[e.user_id]?.[m.id];
                    return (
                      <td key={m.id} className="text-center px-1 py-1">
                        <button
                          disabled={e.is_super_admin}
                          onClick={() => setEditUser(e)}
                          data-testid={`matrix-cell-${e.user_id}-${m.id}`}
                          className={`w-8 h-8 rounded-md text-[13px] font-semibold transition ${
                            on
                              ? "bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200"
                              : "bg-slate-100 text-slate-300 border border-slate-100 hover:bg-slate-200"
                          } ${e.is_super_admin ? "cursor-not-allowed opacity-70" : ""}`}
                          title={on ? "Has access — click to edit" : "No access — click to grant"}>
                          {on ? <Check size={14} strokeWidth={2} className="inline" /> : "·"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editUser && (
        <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setEditUser(null)}
          data-testid="matrix-edit-modal">
          <div className="absolute right-0 top-0 h-full w-[720px] max-w-full bg-white shadow-2xl flex flex-col"
            onClick={(ev) => ev.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Edit access — {editUser.employee_name}</h3>
                <p className="text-[12px] text-slate-500">{editUser.email}</p>
              </div>
              <button onClick={() => setEditUser(null)} className="p-2 rounded hover:bg-slate-100" data-testid="matrix-edit-close">
                <X className="w-4 h-4 text-slate-500"/>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <VisualAccessManager
                userId={editUser.user_id}
                allEmployees={data.employees}
                onSaved={() => { load(); }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
